/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { fileViewModes } from "./constants";
import { untranslated } from "@Helpers/types/i18n";
import { fileManagerBreadCrumb } from "@Actions/fileUpload";
import type { BreadcrumbItem } from "@ServerTypes/folders/folders.schema";
import type {
  FileManagerData,
  FileManagerRequestArgs,
  FileManagerRow,
  FileManagerStateUpdate,
  FileManagerSuccessArgs,
  FileSystemReadResponse,
  FileSystemDeleteResponse,
} from "./fileSystemServer.schema";
import type { FileManagerHandle } from "./FileManagerComponent";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * The file-system request payload this hook builds. The caller's `beforeSending` hook receives it and
 * mutates it in place, which is where `cancel` / `useAttachedExternalVolume` come from.
 */
interface FileSystemParams {
  action: string;
  path: string;
  data?: unknown[];
  bulkParentIds?: string[];
  caseSensitive?: boolean;
  searchString?: string;
  cancel?: boolean;
  useAttachedExternalVolume?: boolean;
  includeDirectoryTotal?: boolean;
  directoryTotalProjectId?: string;
}

/**
 * SCHEMA GAP: `folderPermissionSchema` declares only the lowercase `read`/`write`/... flags, but
 * file-manager rows also carry the uppercase `FOLDER_*` permission constants.
 */
type RowPermissions = NonNullable<FileManagerRow["permission"]> & { FOLDER_DELETE?: boolean };

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */

/**
 * The ONE place the server's file rows are read as `FileManagerRow`.
 *
 * SCHEMA GAP: `crudFileItemSchema` / `readCwdSchema`
 * (packages/server/fileSystemServer/fileSystemServer.schema.ts) type `dateCreated`/`dateModified` as
 * `string | number` and `hierarchyId` as a plain string, where `FileManagerRow` (through
 * `folderItemSchema`'s `SelectedFileItem`) declares `number` and `ObjectIdString | "0"`. Nothing is
 * converted here — the rows are the same objects — so the view is taken once instead of at each of
 * the six reads below.
 * Impact: latent — this hook reads none of those three fields, but a caller that formats
 * `dateModified` as Unix ms would get an ISO string from any route that sends one.
 * Blocked on: narrowing those fields in `crudFileItemSchema` to match `folderItemSchema`.
 */
function toFileManagerRow(row: Record<string, unknown>): FileManagerRow {
  return row as FileManagerRow;
}

/** Rows form of {@link toFileManagerRow}; an absent listing reads as an empty one. */
const toFileManagerRows = (rows: readonly Record<string, unknown>[] | null | undefined): FileManagerRow[] =>
  (rows ?? []).map(toFileManagerRow);

interface UseFileManagerComponentProps {
  fileManagerData?: FileManagerData;
  setFileManagerData(update: FileManagerStateUpdate<FileManagerData>): void;
  setCurrentFolder?(folder: FileManagerStateUpdate<FileManagerRow>): void;
  beforeSending?(args: FileManagerRequestArgs): void;
  onSuccess?(args: FileManagerSuccessArgs): void;
  showFolderTree?: boolean;
  path?: string;
  workspaceId?: string;
  onDeleteSuccess?(files: FileManagerRow[]): void;
  /**
   * No consumer supplies this today, so the parameter is declared as the call sites actually pass
   * it: the server's `message`/`errorMessage` are both optional.
   */
  onDeleteError?(message: string | undefined): void;
  disableAutoLoad?: boolean;
  // Project-level ("Folders") mode: the tree shows every Space of the project as a top-level root. The read
  // hits /readFiles with a projectId, returning all Space roots + only the focus Space's first level (lazy);
  // other Spaces and folders load their contents on demand when expanded.
  projectMode?: boolean;
  projectId?: string;
  focusSpaceId?: string | undefined;
}

function useFileManagerComponent({
  fileManagerData = { cwd: {}, files: [] },
  setFileManagerData,
  setCurrentFolder = () => {},
  beforeSending = () => {},
  onSuccess = () => {},
  showFolderTree = true,
  path,
  workspaceId,
  onDeleteSuccess = () => {},
  onDeleteError = () => {},
  disableAutoLoad = false,
  projectMode = false,
  projectId,
  focusSpaceId,
}: UseFileManagerComponentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State — in project mode nothing is selected until a Space root is picked (the '0' sentinel means
  // "the single space root", which is ambiguous when there are many Space roots).
  const [selectedFolder, setSelectedFolder] = useState<FileManagerRow>(projectMode ? {} : { id: "0" });
  const [filesViewMode, setFilesViewMode] = useState(fileViewModes.DETAILED_VIEW);
  const [tempFolder, setTempFolder] = useState<FileManagerRow>(projectMode ? {} : { id: "0" });
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [selectedFilesInIconsMode, setSelectedFilesInIconsMode] = useState<FileManagerRow[]>([]);
  const [isViewRendered, setIsViewRendered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Refs
  const splitterSizeRef = useRef([30, 70]);
  // Returned below but nothing destructures it; typed rather than left as an untyped bag.
  const tempRef = useRef<Record<string, unknown>>({});
  const treeViewRef = useRef(null);
  // Project mode loads the whole tree once (eager); navigating updates the URL hash → path, but that must
  // NOT re-trigger a read (it caused a double fetch + grid flash). This guards the [path] effect.
  const projectLoadedRef = useRef(false);

  // A Space-root tree node: top-level, identity is its real spaceId (not the '0' sentinel). Only exists
  // in project mode; replaces the old `id === "0"` root check everywhere it mattered.
  const isSpaceRoot = (node: FileManagerRow) => !!(projectMode && node && node.isSpaceRoot && node.spaceId);

  // Handlers
  const updateCurrentDir = (directory: FileManagerRow) => {
    setCurrentFolder(directory);
    setSelectedFolder(directory);
    setTempFolder(directory);
  };

  // Live handle to the loaded tree so navigateToFolder (exposed via the component ref for the app-header
  // breadcrumb) always resolves against the latest files, not a stale render's data.
  const fileManagerDataRef = useRef(fileManagerData);
  fileManagerDataRef.current = fileManagerData;

  // Navigate to a folder by id — the app-header breadcrumb calls this. Resolves the full tree node from the
  // loaded files and selects it, i.e. the same path a grid/tree click takes (the [selectedFolder] effect
  // then fetches its children and settles current/temp folder).
  const navigateToFolder = (folderId: string) => {
    const folder = (fileManagerDataRef.current?.files || []).find((f: FileManagerRow) => f.id === folderId);
    if (folder) setSelectedFolder(folder);
  };

  const generatePath = (folder: FileManagerRow) => {
    const innerPath = folder.parentIdArray?.filter((id: string) => id != "0").join("/");
    const prefixPath = innerPath ? `/${innerPath}` : "";
    const suffixPath = isSpaceRoot(folder) || folder.id == "0" ? "" : `${selectedFolder.id}/`;
    const finalPath = `${prefixPath}/${suffixPath}`;
    return finalPath;
  };

  // The folder tree's footer shows a fixed total (Space roots + every folder beneath them), not the rows
  // lazily loaded so far, so the server has to recount whenever the tree can have changed size. In project
  // mode the tree spans every Space, so the total is scoped to the project rather than to the one Space the
  // request happens to be reading.
  const directoryTotalParams = () => ({
    includeDirectoryTotal: true,
    ...(projectMode && projectId ? { directoryTotalProjectId: projectId } : {}),
  });

  const fetchData = async (allIds: string[] | null = null, setDirectory = true) => {
    const allParams: FileSystemParams = {
      action: "read",
      path: "/",
    };
    if (projectMode) {
      // Project ("Folders") mode: ONE read by projectId returns every Space as a tree root PLUS only the
      // focus Space's contents (lazy — other Spaces load on expand). The Space comes from `focusSpaceId`
      // (the URL's `spaceId` query param); `allIds` is the deep-linked folder chain, loaded so a nested
      // folder can be re-selected. Skips the single-space beforeSending stamping.
      const chain = allIds ?? [];
      allParams.data = [{ projectId, ...(focusSpaceId ? { spaceId: focusSpaceId } : {}) }];
      if (chain.length) {
        allParams.bulkParentIds = chain;
      }
    } else {
      if (allIds) {
        allParams.bulkParentIds = allIds;
      }
      Object.assign(allParams, directoryTotalParams());
      beforeSending(allParams);
    }
    if (!allParams.cancel) {
      let allFiles: FileManagerRow[] = [];
      setRefreshGrid(true);
      // External-volume reads STREAM from a dedicated route; normal reads use /readFiles.
      const fsUrl = allParams.useAttachedExternalVolume
        ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
        : `${getBackendUrl()}/fileSystemServer/readFiles`;
      const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
        ...allParams,
      });
      setIsLoaded(true);
      if (res.data.files) {
        // cwd is the Space the server chose to open (default Space, else the first accessible one
        // alphabetically); in single-space mode it's the '0' root. updateCurrentDir selects it → the
        // [selectedFolder] effect seeds the grid. projectLoaded lets the page show the "not part of any
        // Space" state when the caller can access none (cwd empty).
        const directory = toFileManagerRow({ ...res.data.cwd });
        setFileManagerData({
          ...res.data,
          cwd: res.data.cwd ? toFileManagerRow(res.data.cwd) : {},
          files: toFileManagerRows(res.data.files),
          customFieldsByFolder: projectMode ? {} : { "0": res.data.customFields ?? [] },
          ...(projectMode ? { projectLoaded: true } : {}),
        });
        allFiles = toFileManagerRows(res.data.files);
        if (setDirectory) {
          // Project mode: the focus Space's contents already arrived with this read, so mark the selection
          // isBulkFetch to skip the redundant on-demand re-fetch in the [selectedFolder] effect.
          // Single-space: force the cwd's id to the '0' root sentinel (getFileAttributes only sets
          // hierarchyId, leaving id undefined) so it matches the tree's synthetic root — the Space then
          // renders in the selected state with its root files shown.
          updateCurrentDir(projectMode ? { ...directory, isBulkFetch: true } : { ...directory, id: "0" });
        }
        onSuccess({
          result: res.data,
          action: "read",
          path: "/",
        });
      } else if (res.data.errorMessage) {
        if (projectMode) {
          setFileManagerData({ cwd: {}, files: [], projectLoaded: true });
        } else if (res.data.cwd) {
          const directory = toFileManagerRow({ ...res.data.cwd, id: "0", isRowDisabled: true });
          setFileManagerData({
            cwd: directory,
            files: [],
          });
        }
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
      return allFiles;
    }
    return undefined;
  };

  const getMoreData = async (refreshDirectoryTotal = false) => {
    setRefreshGrid(true);
    const payload = { ...selectedFolder };
    const allParams: FileSystemParams = {
      action: "read",
      path: "/",
      data: [payload],
    };
    if (selectedFolder.id != "0") {
      const fetchPath = generatePath(selectedFolder);
      allParams.path = fetchPath;
    }
    if (refreshDirectoryTotal) {
      Object.assign(allParams, directoryTotalParams());
    }
    beforeSending(allParams);
    if (!allParams.cancel) {
      const fsUrl = allParams.useAttachedExternalVolume
        ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
        : `${getBackendUrl()}/fileSystemServer/readFiles`;
      const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
        ...allParams,
      });
      if (res.data?.files) {
        // Project mode: re-parent the fetched items into the multi-space tree. A Space-root read returns
        // top-level items with parentId "0", which must nest under the Space-root id; stamp spaceId on all
        // so the tree/grid filters and later writes resolve the right Space.
        const incomingFiles = ((res.data?.files || []) as FileManagerRow[]).map((file: FileManagerRow) =>
          projectMode
            ? {
                ...file,
                spaceId: selectedFolder.spaceId,
                parentId: file.parentId === "0" ? selectedFolder.spaceId : file.parentId,
              }
            : file,
        );
        const incomingFileIds = incomingFiles?.map((file: FileManagerRow) => file.id) || [];
        let freshCurrentFolder = { ...selectedFolder };
        // Single-space only: adopt the server cwd as the current node (a folder read returns its own
        // descriptor). In project mode the tree node is authoritative (already re-parented + spaceId), so
        // keep selectedFolder as the current folder.
        // @ts-expect-error -- `_id` is read off a row narrowed to the folder-item shape, which declares `id` but not `_id`. SCHEMA GAP: the server sends both and only one is declared.
        if (!projectMode && res.data?.cwd?._id && res.data?.cwd?._id != "0") {
          incomingFileIds.push(selectedFolder.id);
          freshCurrentFolder = res.data?.cwd as FileManagerRow;
          incomingFiles.push(freshCurrentFolder);
        }
        // KNOWN BUG, preserved verbatim: `files` may be absent, in which case the second `.filter`
        // throws. Erased assertion; a guard would change behaviour.
        let currentFilteredFiles = fileManagerData.files?.filter(
          (file: FileManagerRow) => !incomingFileIds.includes(file.id),
        );
        currentFilteredFiles = currentFilteredFiles!.filter(
          (file: FileManagerRow) => file.parentId != selectedFolder.id,
        );
        setFileManagerData((prev: FileManagerData) => ({
          ...prev,
          ...(res.data.totalDirectoryCount === undefined ? {} : { totalDirectoryCount: res.data.totalDirectoryCount }),
          files: [...incomingFiles, ...currentFilteredFiles!],
          customFieldsByFolder: {
            ...((prev.customFieldsByFolder as Record<string, unknown>) || {}),
            // The cwd always carries an id here; a computed key may not be `undefined`.
            [freshCurrentFolder.id as string]: res.data?.customFields ?? [],
          },
        }));
        onSuccess({
          result: res.data,
          action: "read",
          path: allParams.path,
        });
        setCurrentFolder(freshCurrentFolder);
        setTempFolder(freshCurrentFolder);
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data?.errorMessage || ""),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
    }
  };

  const fetchMoreData = async () => {
    if (projectMode) {
      if (!selectedFolder?.id) return;
      await getMoreData();
      return;
    }
    if (selectedFolder.id !== "0") {
      await getMoreData();
    } else {
      setTempFolder(selectedFolder);
      setCurrentFolder(selectedFolder);
    }
  };

  const refreshFileManager = () => {
    fetchData();
    if (!projectMode) setSelectedFolder({ id: "0" });
  };

  const refreshFiles = async () => {
    await getMoreData(true);
  };

  function extractIds() {
    return (path || "")
      .split("/")
      .filter(Boolean)
      .filter((id) => id != "0" && objectIdString.safeParse(id).success);
  }

  const fetchBulkData = async () => {
    const allIds: string[] = extractIds();
    const allFiles: FileManagerRow[] | undefined = await fetchData(allIds, false);
    const lastFolderId: string | undefined = allIds[allIds.length - 1];
    let lastFolder = allFiles?.find((file: FileManagerRow) => file.id == lastFolderId);
    // Project mode: if the deep-linked node is gone (deleted / no longer accessible / stale hash), fall back
    // to the focus Space root — or, with no focus Space in the URL, to the default one the server opened —
    // so the tree lands somewhere valid instead of selecting nothing.
    if (!lastFolder && projectMode) {
      const spaceRoots = (allFiles ?? []).filter((file: FileManagerRow) => file.isSpaceRoot);
      lastFolder = focusSpaceId
        ? spaceRoots.find((file: FileManagerRow) => file.id == focusSpaceId)
        : spaceRoots.find((file: FileManagerRow) => file.isDefault);
    }
    if (lastFolder) {
      updateCurrentDir({ ...lastFolder, isBulkFetch: true });
    }
  };

  const handleSearchFiles = async (args: string | null) => {
    if (args) {
      setRefreshGrid(true);
      const payload = { ...selectedFolder };
      const allParams: FileSystemParams = {
        action: "search",
        caseSensitive: false,
        searchString: `*${args}*`,
        path: "/",
        data: [payload],
      };
      // Only narrow the search path to a specific folder when one is actually focused. A pre-search
      // can fire before the deep-link Space focus lands (selectedFolder still empty in project mode);
      // in that case keep path "/" for a Space-wide search rather than a malformed "/undefined/" path.
      if (selectedFolder.id && selectedFolder.id != "0") {
        const fetchPath = generatePath(selectedFolder);
        allParams.path = fetchPath;
      }
      beforeSending(allParams);
      if (!allParams.cancel) {
        delete allParams.cancel;
        // Search over an external volume streams from /readExternalVolume; otherwise /searchFiles.
        // (the search Space itself is stamped onto the payload data by beforeSending.)
        const fsUrl = allParams.useAttachedExternalVolume
          ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
          : `${getBackendUrl()}/fileSystemServer/searchFiles`;
        const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
          ...allParams,
        });
        if (res.data) {
          setFileManagerData((prev: FileManagerData) => ({
            ...prev,
            filteredFiles: [...((res.data.files ?? []) as FileManagerRow[])],
          }));
        }
        onSuccess({
          result: res.data,
          action: "search",
          path: allParams.path,
        });
        setRefreshGrid(false);
      }
    } else {
      setFileManagerData((prev: FileManagerData) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  };

  const getSelectedFilesInFileManager = (filesRef: React.RefObject<FileManagerHandle | null>) => {
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      // KNOWN BUG, preserved verbatim: neither the ref nor its dataView is guarded here, so calling
      // this before the grid mounts throws. Erased assertions only.
      const selectedFile = filesRef.current!.dataView!.getAllSelectedItems();
      return selectedFile.filter(Boolean);
    } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
      return selectedFilesInIconsMode.filter(Boolean);
    }
    return [];
  };

  const showLoading = () => {
    setRefreshGrid(true);
  };

  const hideLoading = () => {
    setRefreshGrid(false);
  };

  // Delete files/folders
  const deleteFiles = async (filesToDelete: FileManagerRow[], deleteAssociatedTasks = true) => {
    if (!filesToDelete || filesToDelete.length === 0) return { success: false };

    try {
      const selectedFilesToDelete = filesToDelete.map((file: FileManagerRow) => ({
        ...file,
        // Prefer the file's own space (project mode spans many spaces); fall back to the single workspace.
        spaceId: file.spaceId ?? workspaceId,
        deleteAssociatedTasks,
      }));

      const data = {
        action: "delete",
        data: selectedFilesToDelete,
        path: "/",
      };

      beforeSending(data);

      const response = await axiosWrapper.post<FileSystemDeleteResponse>(
        `${getBackendUrl()}/fileSystemServer/deleteFiles`,
        data,
      );

      if (response.data.status) {
        // Remove deleted files from local state
        const deletedIds = filesToDelete.map((f: FileManagerRow) => f.id);
        setFileManagerData((prev: FileManagerData) => ({
          ...prev,
          files: prev.files?.filter((f: FileManagerRow) => !deletedIds.includes(f.id)) || [],
        }));

        onDeleteSuccess(filesToDelete);
        dispatch(
          showToastNotification({
            content: filesToDelete.length === 1 ? t("Item deleted successfully") : t("Items deleted successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        return { success: true };
      } else {
        onDeleteError(response.data.message || response.data.errorMessage);
        dispatch(
          showToastNotification({
            content: untranslated(response.data.message || response.data.errorMessage || "Failed to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return { success: false, error: response.data.message };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : undefined;
      onDeleteError(errMsg || "Failed to delete");
      dispatch(
        showToastNotification({
          content: t("Failed to delete item"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return { success: false, error: errMsg };
    }
  };

  // Check if file can be deleted
  const canDeleteFile = (file: FileManagerRow) => {
    return (file?.permission as RowPermissions | null | undefined)?.FOLDER_DELETE === true;
  };

  // Get current folder files (filtered and sorted)
  const getCurrentFolderFiles = (searchQuery = "") => {
    const data = fileManagerData.filteredFiles || fileManagerData.files || [];
    let items: FileManagerRow[];

    if (tempFolder?.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      items = data.filter(
        (item: FileManagerRow) =>
          item.transmittalVersionId === tempFolder.id && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
      );
    } else if (!tempFolder?.resourceType || tempFolder?.id === "0") {
      items = data.filter((item: FileManagerRow) => item.parentId === tempFolder?.id);
    } else {
      items = data.filter((item: FileManagerRow) => item.parentId === tempFolder?.id);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      items = items.filter((item: FileManagerRow) => item.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Sort: folders first, then files, alphabetically
    const folders = items.filter(
      (item: FileManagerRow) =>
        item.resourceType === "RESOURCE_TYPE_FOLDER" || item.resourceType === "RESOURCE_TYPE_TRANSMITTAL",
    );
    const files = items.filter(
      (item: FileManagerRow) =>
        item.resourceType !== "RESOURCE_TYPE_FOLDER" && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
    );

    // KNOWN BUG, preserved verbatim: a row with no `name` makes the comparator return `undefined`
    // instead of a number, which leaves the sort order unspecified. Erased assertions only.
    const sortedFolders = [...folders].sort(
      (a: FileManagerRow, b: FileManagerRow) => a.name?.localeCompare(b.name as string) as number,
    );
    const sortedFiles = [...files].sort(
      (a: FileManagerRow, b: FileManagerRow) => a.name?.localeCompare(b.name as string) as number,
    );

    return [...sortedFolders, ...sortedFiles];
  };

  // Check if item is a folder
  const isFolder = (item: FileManagerRow) => {
    return item?.resourceType === "RESOURCE_TYPE_FOLDER" || item?.resourceType === "RESOURCE_TYPE_TRANSMITTAL";
  };

  // Check if file can be viewed
  const canViewFile = (file: FileManagerRow) => {
    return file?.permission?.read !== false;
  };

  // Effects
  useEffect(() => {
    let size: number[];
    if (!showFolderTree) {
      size = [0, 100];
    } else {
      size = [30, 70];
    }
    splitterSizeRef.current = size;
  }, [showFolderTree]);

  useEffect(() => {
    if (disableAutoLoad) return;
    if (projectMode) {
      // Load the tree ONCE. Subsequent path changes (from folder navigation updating the #folderPath hash)
      // must not re-fetch — that fired the read twice and cleared the grid.
      if (projectLoadedRef.current) return;
      const allIds = extractIds();
      // On a browser refresh the folder path lands in `path` a tick after mount (null -> "/" -> #hash). If the
      // URL still carries a deep-link hash whose ids haven't arrived in `path` yet, wait — otherwise we'd load
      // the default Space now and the once-guard below would then block restoring the previously-open folder.
      const hasPendingDeepLink = typeof window !== "undefined" && window.location.hash.includes("folderPath");
      if (hasPendingDeepLink && !allIds.length) return;
      projectLoadedRef.current = true;
      // The hash carries [spaceId, ...folderChain]: focus that Space and restore the previously-open node
      // (bulkParentIds loads its ancestor chain). With no hash, open the default/first accessible Space.
      if (allIds.length) {
        fetchBulkData();
      } else {
        fetchData();
      }
      return;
    }
    if (path) {
      const allIds = extractIds();
      if (allIds.length) {
        fetchBulkData();
      } else {
        fetchData();
      }
    }
  }, [path]);

  useEffect(() => {
    if (disableAutoLoad) return;
    const customFieldsLoaded = Boolean(
      (fileManagerData?.customFieldsByFolder as Record<string, unknown> | undefined)?.[selectedFolder?.id as string],
    );
    if (!selectedFolder.isBulkFetch || !customFieldsLoaded) {
      fetchMoreData();
      setSelectedFilesInIconsMode([]);
      setFileManagerData((prev: FileManagerData) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  }, [selectedFolder?.id]);

  // Project ("Folders") mode: publish the current folder path to redux so the FileExplorer page can mirror
  // it into the app-header breadcrumb. Crumbs are minimal {id,name}; a click resolves the full node by id
  // via navigateToFolder. Path shape mirrors the space-root breadcrumb in useFileManagerShared:
  // [Space root, ...ancestor folders, current folder].
  useEffect(() => {
    if (!projectMode) return;
    const files = fileManagerData?.files || [];
    const folder = selectedFolder;
    // Typed from the schema (BreadcrumbItem) rather than re-declared inline: this array is dispatched
    // straight into the fileManagerBreadCrumb slice, so the branded id type must come from the source.
    const crumbs: BreadcrumbItem[] = [];
    if (folder?.id) {
      const isCurrentSpaceRoot = !!(folder?.isSpaceRoot && folder?.spaceId);
      const spaceRoot = files.find((f: FileManagerRow) => f?.isSpaceRoot && f.spaceId === folder?.spaceId);
      // Tree ids are plain strings; `BreadcrumbItem.id` is the branded objectIdString, so each is
      // asserted at this dispatch boundary.
      if (spaceRoot) crumbs.push({ id: spaceRoot.id as BreadcrumbItem["id"], name: spaceRoot.name });
      (folder?.parentIdArray || []).forEach((id: string) => {
        const obj = files.find((item: FileManagerRow) => item.id === id && !item.isSpaceRoot);
        if (obj) crumbs.push({ id: obj.id as BreadcrumbItem["id"], name: obj.name });
      });
      if (!isCurrentSpaceRoot) crumbs.push({ id: folder.id as BreadcrumbItem["id"], name: folder.name });
    }
    dispatch(fileManagerBreadCrumb(crumbs));
  }, [selectedFolder?.id, fileManagerData?.files, projectMode]);

  // Clear the published path on unmount so it doesn't leak into other surfaces (projectMode only, so a
  // popup/single-space file manager unmounting can't wipe the Folders page's breadcrumb).
  useEffect(
    () => () => {
      if (projectMode) dispatch(fileManagerBreadCrumb([]));
    },
    [],
  );

  return {
    // State
    selectedFolder,
    setSelectedFolder,
    filesViewMode,
    setFilesViewMode,
    tempFolder,
    setTempFolder,
    refreshGrid,
    setRefreshGrid,
    selectedFilesInIconsMode,
    setSelectedFilesInIconsMode,
    isViewRendered,
    setIsViewRendered,
    isLoaded,
    setIsLoaded,

    // Refs
    splitterSizeRef,
    tempRef,
    treeViewRef,

    // Handlers
    updateCurrentDir,
    navigateToFolder,
    generatePath,
    fetchData,
    getMoreData,
    fetchMoreData,
    refreshFileManager,
    refreshFiles,
    extractIds,
    fetchBulkData,
    handleSearchFiles,
    getSelectedFilesInFileManager,
    showLoading,
    hideLoading,

    // File operations
    deleteFiles,
    canDeleteFile,
    getCurrentFolderFiles,
    isFolder,
    canViewFile,

    // Props passthrough
    workspaceId,
  };
}

export default useFileManagerComponent;
